import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email?: string;
}

interface AttendanceRecord {
  studentId: string;
  signInTime: Date | null;
  signOutTime: Date | null;
  signInName: string;
  signOutName: string;
}

interface AttendanceBatchDialogProps {
  isOpen: boolean;
  title: string;
  students: Student[];
  attendanceRecords: Record<string, AttendanceRecord>;
  onClose: () => void;
  onConfirm: (name: string, selectedStudentIds: string[]) => void;
  defaultName?: string;
  isLoading?: boolean;
}

const AttendanceBatchDialog: React.FC<AttendanceBatchDialogProps> = ({
  isOpen,
  title,
  students,
  attendanceRecords,
  onClose,
  onConfirm,
  defaultName = '',
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(defaultName);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());

  // 获取未签到的学生列表
  const unSignedInStudents = students.filter(student => {
    const record = attendanceRecords[student.id];
    return !record?.signInTime;
  });

  // 初始化时选择所有未签到的学生
  useEffect(() => {
    if (isOpen) {
      const unSignedInIds = unSignedInStudents.map(student => student.id);
      setSelectedStudents(new Set(unSignedInIds));
    }
  }, [isOpen, unSignedInStudents.length]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && selectedStudents.size > 0 && !isLoading) {
      onConfirm(name.trim(), Array.from(selectedStudents));
      setName('');
      setSelectedStudents(new Set());
    }
  };

  const handleSelectAll = () => {
    const allUnSignedInIds = unSignedInStudents.map(student => student.id);
    setSelectedStudents(new Set(allUnSignedInIds));
  };

  const handleDeselectAll = () => {
    setSelectedStudents(new Set());
  };

  const handleStudentToggle = (studentId: string) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const isAllSelected = selectedStudents.size === unSignedInStudents.length && unSignedInStudents.length > 0;
  const isPartialSelected = selectedStudents.size > 0 && selectedStudents.size < unSignedInStudents.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-xl w-full max-w-2xl mx-4 transform transition-all max-h-[80vh] flex flex-col" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {title}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          {/* 签到人姓名输入 */}
          <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
            <label 
              htmlFor="name" 
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('attendance.nameLabel')}
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              autoFocus
              required
              disabled={isLoading}
              placeholder={t('attendance.namePlaceholder')}
            />
          </div>

          {/* 学生选择区域 */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {/* 全选/全不选操作栏 */}
            <div className="flex-shrink-0 p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isPartialSelected;
                      }}
                      onChange={isAllSelected ? handleDeselectAll : handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={isLoading || unSignedInStudents.length === 0}
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('attendance.selectAll')}
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    disabled={isLoading || selectedStudents.size === 0}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('attendance.deselectAll')}
                  </button>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('attendance.selectedCount').replace('{count}', selectedStudents.size.toString()).replace('{total}', unSignedInStudents.length.toString())}
                </span>
              </div>
            </div>

            {/* 学生列表 */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {unSignedInStudents.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    {t('attendance.allStudentsSignedIn')}
                  </p>
                </div>
              ) : (
                <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {unSignedInStudents.map((student) => (
                    <label
                      key={student.id}
                      className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedStudents.has(student.id)}
                        onChange={() => handleStudentToggle(student.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={isLoading}
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {student.firstName} {student.lastName}
                        </div>
                        {student.phoneNumber && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {student.phoneNumber}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 底部操作按钮 */}
          <div className="flex-shrink-0 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={!name.trim() || selectedStudents.size === 0 || isLoading}
                className="relative px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
              >
                {isLoading ? (
                  <>
                    <span className="invisible">
                      {t('attendance.batchSignInConfirm', { count: selectedStudents.size })}
                    </span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  </>
                ) : (
                  t('attendance.batchSignInConfirm').replace('{count}', selectedStudents.size.toString())
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceBatchDialog;
